import { isSupportedCountry, parsePhoneNumberFromString } from "libphonenumber-js";
import { countries } from "../../../PaymentPages/countryData";

/**
 * Dial code for a `phone` form field, mirroring the console's three-step
 * fallback in FormStepCanvas: the field's own configured country, then the
 * region default, then India.
 *
 * `countryData` is the buyer app's existing 132-country list; the builder's
 * picker offers a 40-country subset of it, so every selectable country
 * resolves. If one ever does not, the region fallback covers it rather than
 * rendering a blank prefix next to the input.
 */
export const resolveDialCode = (field, region) => {
  const code = field && field.phoneConfig && field.phoneConfig.defaultCountry;
  const match = code ? countries.find((c) => c.code === code) : null;
  return (match && match.dial_code) || (region && region.defaultDialCode) || "+91";
};

/**
 * Country object for a phone field, mirroring resolveDialCode's fallback chain
 * but returning the whole record so the flag is available too.
 */
export const resolveCountry = (field, region) => {
  const code = field && field.phoneConfig && field.phoneConfig.defaultCountry;
  const byCode = code ? countries.find((c) => c.code === code) : null;
  if (byCode) return byCode;
  const dial = (region && region.defaultDialCode) || "+91";
  return (
    countries.find((c) => c.dial_code === dial) || { code: "", dial_code: dial, flag: "" }
  );
};

/**
 * The list the picker offers. The affiliate can restrict a field to specific
 * markets via phoneConfig.allowedCountries; an empty or missing list means no
 * restriction. The resolved default is always included, so a default outside
 * the allowed list still renders instead of showing a blank prefix.
 */
export const countryOptionsFor = (field, region) => {
  const allowed =
    (field && field.phoneConfig && field.phoneConfig.allowedCountries) || [];
  const base = allowed.length
    ? countries.filter((c) => allowed.includes(c.code))
    : countries;
  const def = resolveCountry(field, region);
  // The prefix is a <select>, so its value must match an option or it renders
  // blank. An unrecognised region dial code yields a country with no `code`, so
  // prepend it too — showing the bare dial code beats showing nothing.
  return base.some((c) => c.code === def.code) ? base : [def, ...base];
};

/**
 * Inline error for a phone value (national digits, no dial code) in the picked
 * country, or null if it is valid. Uses the same libphonenumber rules as the
 * server; India must additionally be a 10-digit mobile. A country the
 * library doesn't know falls back to a loose 6-14 digit check.
 */
export const phoneErrorFor = (countryCode, digits) => {
  if (countryCode === "IN") {
    return /^[6-9]\d{9}$/.test(digits)
      ? null
      : "Enter a valid 10-digit Indian mobile number";
  }
  if (!countryCode || !isSupportedCountry(countryCode)) {
    return /^\d{6,14}$/.test(digits) ? null : "Enter a valid phone number";
  }
  const parsed = parsePhoneNumberFromString(digits, countryCode);
  if (parsed && parsed.isValid()) return null;
  const country = countries.find((c) => c.code === countryCode);
  return country
    ? `Enter a valid phone number for ${country.name}`
    : "Enter a valid phone number";
};

export default resolveDialCode;
